<template>
  <button
    :class="[$style.chatButton]"
    @click="!isLoading && $emit('toggle-expand')"
    :disabled="isLoading"
    aria-label="Toggle chat widget"
  >
    <!-- 로딩 스피너 -->
    <svg v-if="isLoading" :class="$style.spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
      <path
        class="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>

    <!-- 기존 아이콘 (로딩 아닐 때) -->
    <template v-else>
      <img v-if="!expanded" :src="chatIconUrl" alt="Chat Icon" :class="$style.icon" />
      <img
        v-else
        :src="closeIconUrl"
        alt="Close Icon"
        :class="$style.icon"
        style="margin: 4px 0 0 0; width: 20px; height: 20px"
      />
    </template>

    <!-- 안 읽은 메시지 카운트 배지 (expanded가 아니고 로딩 아닐 때) -->
    <span v-if="!expanded && !isLoading && (unreadCount || 0) > 0" :class="$style.badge">
      {{ displayUnreadCount }}
    </span>
  </button>
</template>

<script setup lang="ts">
import { computed, useCssModule } from "vue"
// SVG 파일 import 경로 수정 (@ 앨리어스 재시도)
import chatIconUrl from "@/assets/icons/chat-icon.svg"
import closeIconUrl from "@/assets/icons/close.svg"

const props = defineProps<{
  isLoading?: boolean // isLoading prop 추가
  unreadCount?: number
  expanded?: boolean // expanded prop 추가
}>()

// click 대신 toggle-expand 이벤트 emit
defineEmits(["toggle-expand"])

const $style = useCssModule()

// 표시될 안읽음 카운트 (예: 99+)
const displayUnreadCount = computed(() => {
  const count = props.unreadCount || 0
  return count > 99 ? "99+" : count
})
</script>

<style module scoped>
.chatButton {
  /* Figma 디자인 기반 스타일 업데이트 */
  width: 64px; /* Figma: 64px */
  height: 64px; /* Figma: 64px */
  border-radius: 50%; /* Figma: Ellipse */
  background-color: #ffffff; /* Figma: #FFFFFF */
  color: white; /* 아이콘 색상에 따라 불필요할 수 있음 */
  border: 0.5px solid rgba(207, 21, 120, 0.2); /* Figma: stroke_FXGB4I */
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Figma: effect_0F9O3U */
  box-shadow:
    0px 4px 4px -4px rgba(12, 12, 13, 0.05),
    0px 16px 32px -4px rgba(12, 12, 13, 0.1);
  transition:
    transform 0.2s ease-out,
    box-shadow 0.2s ease-out;
  position: relative; /* 배지 및 스피너 위치 기준 */
}

.chatButton:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.chatButton:disabled {
  cursor: not-allowed;
  opacity: 0.7; /* 로딩/비활성 상태 시 약간 투명하게 */
}

.icon {
  width: 40px;
  height: 40px;
  margin-right: 1px;
  margin-top: 2px;
}

.spinner {
  animation: spin 1s linear infinite;
  width: 24px; /* 아이콘 크기와 유사하게 조정 */
  height: 24px;
  color: var(--wowpress-primary-color, #cf1578); /* 테마 색상 사용 */
}

.badge {
  position: absolute;
  top: -4px; /* 위치 조정 */
  right: -4px; /* 위치 조정 */
  background-color: red;
  color: white;
  border-radius: 50%;
  padding: 2px 6px;
  font-size: 12px;
  font-weight: bold;
  min-width: 20px; /* 최소 너비 */
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border: 2px solid white; /* 버튼 배경과 구분 */
}

/* 커스텀 테마는 변수를 통해 제어 */

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
